import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { DomSanitizer, SafeResourceUrl, SafeUrl } from '@angular/platform-browser';
import { ButtonComponent, EmptyComponent } from 'nebula-ui-kit';
import { PdfViewerModule } from 'ng2-pdf-viewer';

import { tiffBlobUrlToPngDataUrl } from './vault-tiff-preview.util';

@Component({
  selector: 'app-vault-file-preview',
  standalone: true,
  imports: [ButtonComponent, EmptyComponent, PdfViewerModule],
  template: `
    <div class="preview-backdrop">
      <section class="preview-modal">
        <header class="preview-header">
          <h3>{{ title() || 'Preview de archivo' }}</h3>
          <div class="preview-header__actions">
            @if (showDownloadAction()) {
              <lib-button variant="secondary" (clicked)="downloadFile.emit()">
                {{ downloadLabel() }}
              </lib-button>
            }
            <lib-button variant="secondary" (clicked)="closePreview.emit()">Cerrar</lib-button>
          </div>
        </header>

        <div class="preview-body">
          @if (loading()) {
            <div class="preview-state">
              <lib-empty message="Cargando previsualización" description="Espere por favor..." />
            </div>
          } @else if (errorMessage()) {
            <div class="preview-state preview-state--error">
              <p>{{ errorMessage() }}</p>
              <p>Intenta descargar el archivo para validar su contenido.</p>
            </div>
          } @else if (unsupportedMessage()) {
            <div class="preview-state preview-state--unsupported">
              <p class="preview-unsupported-text">{{ unsupportedMessage() }}</p>
            </div>
          } @else if (isImage() && isTiff()) {
            @if (tiffDecodePhase() === 'error') {
              <div class="preview-image-block">
                <div class="preview-image-zoom-toolbar" role="toolbar" aria-label="Zoom de la imagen">
                  <lib-button
                    variant="secondary"
                    prefixIcon="ZoomOut"
                    [iconSize]="18"
                    ariaLabel="Alejar"
                    [disabled]="!canZoomOut()"
                    (clicked)="zoomOut()"
                  ></lib-button>
                  <span class="preview-image-zoom-value">{{ zoomPercentLabel() }}</span>
                  <lib-button
                    variant="secondary"
                    prefixIcon="ZoomIn"
                    [iconSize]="18"
                    ariaLabel="Acercar"
                    [disabled]="!canZoomIn()"
                    (clicked)="zoomIn()"
                  ></lib-button>
                </div>
                <div class="preview-image-scroll" [style.--preview-zoom]="imageZoom()">
                  <img
                    [src]="displayImageUrl()!"
                    (error)="onImageError()"
                    [alt]="title() || 'Vista previa'"
                  />
                </div>
                @if (imageUseFallback()) {
                  <div class="preview-image-fallback">
                    <p>
                      No se pudo decodificar el TIFF en el navegador. Puede ser un formato o compresión no soportada;
                      descarga el archivo para abrirlo con un visor externo.
                    </p>
                    <lib-button variant="secondary" (clicked)="downloadFile.emit()">Descargar archivo</lib-button>
                  </div>
                }
              </div>
            } @else if (tiffDecodePhase() === 'ready' && tiffPreviewSafeUrl()) {
              <div class="preview-image-block">
                <div class="preview-image-zoom-toolbar" role="toolbar" aria-label="Zoom de la imagen">
                  <lib-button
                    variant="secondary"
                    prefixIcon="ZoomOut"
                    [iconSize]="18"
                    ariaLabel="Alejar"
                    [disabled]="!canZoomOut()"
                    (clicked)="zoomOut()"
                  ></lib-button>
                  <span class="preview-image-zoom-value">{{ zoomPercentLabel() }}</span>
                  <lib-button
                    variant="secondary"
                    prefixIcon="ZoomIn"
                    [iconSize]="18"
                    ariaLabel="Acercar"
                    [disabled]="!canZoomIn()"
                    (clicked)="zoomIn()"
                  ></lib-button>
                </div>
                <div class="preview-image-scroll" [style.--preview-zoom]="imageZoom()">
                  <img [src]="tiffPreviewSafeUrl()!" [alt]="title() || 'Vista previa'" />
                </div>
              </div>
            } @else {
              <div class="preview-state">
                <p>Generando vista previa del TIFF…</p>
              </div>
            }
          } @else if (isPdf()) {
            @if (safeResourceUrl()) {
              <object
                [attr.data]="safeResourceUrl()"
                type="application/pdf"
                class="preview-pdf-object"
              >
                <p>No se pudo renderizar el PDF en el navegador. Usa Descargar para abrirlo.</p>
              </object>
            } @else {
              <pdf-viewer
                [src]="srcData() || srcUrl()"
                [show-all]="true"
                [render-text]="true"
                [original-size]="false"
                [fit-to-page]="true"
              />
            }
          } @else if (isImage() && displayImageUrl()) {
            <div class="preview-image-block">
              <div class="preview-image-zoom-toolbar" role="toolbar" aria-label="Zoom de la imagen">
                <lib-button
                  variant="secondary"
                  prefixIcon="ZoomOut"
                  [iconSize]="18"
                  ariaLabel="Alejar"
                  [disabled]="!canZoomOut()"
                  (clicked)="zoomOut()"
                ></lib-button>
                <span class="preview-image-zoom-value">{{ zoomPercentLabel() }}</span>
                <lib-button
                  variant="secondary"
                  prefixIcon="ZoomIn"
                  [iconSize]="18"
                  ariaLabel="Acercar"
                  [disabled]="!canZoomIn()"
                  (clicked)="zoomIn()"
                ></lib-button>
              </div>
              <div class="preview-image-scroll" [style.--preview-zoom]="imageZoom()">
                <img
                  [src]="displayImageUrl()!"
                  (error)="onImageError()"
                  [alt]="title() || 'Vista previa'"
                />
              </div>
              @if (imageUseFallback()) {
                <div class="preview-image-fallback">
                  <p>
                    Este formato de imagen no se puede previsualizar en el navegador (p. ej. TIFF). Descarga el archivo
                    para abrirlo en una aplicación compatible.
                  </p>
                  <lib-button variant="secondary" (clicked)="downloadFile.emit()">Descargar archivo</lib-button>
                </div>
              }
            </div>
          } @else if (isText()) {
            <pre>{{ textContent() }}</pre>
          } @else if (safeResourceUrl()) {
            <iframe
              [src]="safeResourceUrl()"
              class="preview-generic-frame"
              title="Vista previa de archivo"
            ></iframe>
          } @else {
            <p>No hay vista previa para este tipo de archivo.</p>
          }
        </div>
      </section>
    </div>
  `,
  styles: [
    `
      .preview-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.52);
        display: grid;
        place-items: center;
        z-index: 1200;
        padding: 0.25rem;
      }
      .preview-modal {
        width: min(100%, 98vw);
        max-width: 1600px;
        height: min(100%, 95vh);
        background: var(--md-sys-color-surface, #fff);
        border-radius: 10px;
        border: 1px solid var(--md-sys-color-outline-variant);
        display: flex;
        flex-direction: column;
        overflow: hidden;
      }
      .preview-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        padding: 0.45rem 0.65rem;
        border-bottom: 1px solid var(--md-sys-color-outline-variant);
      }
      .preview-header h3 {
        margin: 0;
        font-size: 1rem;
      }
      .preview-header__actions {
        display: inline-flex;
        align-items: center;
        gap: 0.5rem;
      }
      .preview-body {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        padding: 0;
        overflow: hidden;
      }
      /* ng2-pdf-viewer: :host necesita altura explícita; el interno usa position:absolute; height:100% */
      .preview-body pdf-viewer {
        display: block;
        flex: 1 1 auto;
        width: 100%;
        min-height: 0;
      }
      :host ::ng-deep .preview-body pdf-viewer .ng2-pdf-viewer-container {
        height: 100%;
      }
      .preview-pdf-object,
      .preview-generic-frame {
        flex: 1 1 auto;
        width: 100%;
        min-height: 0;
        height: 100%;
        border: 0;
      }
      .preview-image-block {
        flex: 1;
        min-height: 0;
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: flex-start;
        gap: 0.35rem;
        overflow: hidden;
        padding: 0.35rem 0.5rem 0.5rem;
      }
      .preview-image-zoom-toolbar {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;
        gap: 0.35rem;
        flex-shrink: 0;
      }
      .preview-image-zoom-value {
        font-size: 0.8rem;
        font-variant-numeric: tabular-nums;
        color: var(--md-sys-color-on-surface-variant, #5f6368);
        min-width: 3rem;
        text-align: center;
      }
      .preview-image-scroll {
        flex: 1;
        min-height: 0;
        width: 100%;
        overflow: auto;
        display: flex;
        justify-content: center;
        align-items: flex-start;
      }
      .preview-image-scroll img {
        flex: 0 0 auto;
        display: block;
        width: calc(100% * var(--preview-zoom, 1));
        max-width: none;
        height: auto;
        max-height: none;
        object-fit: contain;
        border-radius: 6px;
      }
      .preview-image-fallback {
        max-width: 28rem;
        text-align: center;
        color: var(--md-sys-color-on-surface-variant, #5f6368);
      }
      .preview-image-fallback p {
        margin: 0 0 0.5rem;
        font-size: 0.9rem;
        line-height: 1.45;
      }
      .preview-body pre {
        margin: 0;
        flex: 1;
        min-height: 0;
        overflow: auto;
        padding: 0.75rem 1rem;
        white-space: pre-wrap;
        word-break: break-word;
      }
      .preview-body > p {
        margin: 0;
        padding: 1rem;
      }
      .preview-state {
        flex: 1;
        min-height: 0;
        display: grid;
        place-items: center;
        padding: 1rem;
        text-align: center;
        color: var(--md-sys-color-on-surface-variant, #5f6368);
      }
      .preview-state--error {
        color: var(--md-sys-color-error, #b3261e);
      }
      .preview-state--unsupported {
        max-width: 28rem;
        margin: 0 auto;
        padding: 1.25rem 1.5rem;
        border-radius: 10px;
        background: var(--md-sys-color-surface-container-highest, #e8eaed);
        border: 1px solid var(--md-sys-color-outline-variant);
        color: var(--md-sys-color-on-surface, #1a1a1a);
      }
      .preview-unsupported-text {
        margin: 0;
        font-size: 0.95rem;
        line-height: 1.45;
        text-align: center;
      }
    `,
  ],
})
export class VaultFilePreviewComponent {
  /** Imagen de respaldo servida desde `assets` (libs/assets → `/assets/…`). */
  private static readonly IMAGE_FALLBACK_SRC = '/assets/default.jpg';

  private static readonly ZOOM_MIN = 0.25;
  private static readonly ZOOM_MAX = 4;
  private static readonly ZOOM_STEP = 0.25;

  private readonly sanitizer = inject(DomSanitizer);
  /** Tras error al cargar la imagen, se muestra el placeholder y el aviso. */
  readonly imageUseFallback = signal(false);
  /** Factor de zoom respecto al ancho del área de vista (1 = 100%). */
  readonly imageZoom = signal(1);
  /** Decodificación TIFF → PNG en cliente (utif). */
  readonly tiffDecodePhase = signal<'idle' | 'loading' | 'ready' | 'error'>('idle');
  readonly tiffPreviewSafeUrl = signal<SafeUrl | null>(null);
  private tiffOpId = 0;

  constructor() {
    effect(() => {
      this.srcUrl();
      untracked(() => {
        this.imageUseFallback.set(false);
        this.imageZoom.set(1);
      });
    });

    effect(() => {
      const url = this.srcUrl();
      const loading = this.loading();
      const tiff = this.isTiff();

      if (!tiff || !url || loading) {
        untracked(() => {
          this.tiffOpId++;
          this.tiffDecodePhase.set('idle');
          this.tiffPreviewSafeUrl.set(null);
        });
        return;
      }

      const opId = ++this.tiffOpId;
      untracked(() => {
        this.tiffDecodePhase.set('loading');
        this.tiffPreviewSafeUrl.set(null);
      });

      void (async () => {
        try {
          const dataUrl = await tiffBlobUrlToPngDataUrl(url);
          if (opId !== this.tiffOpId) {
            return;
          }
          untracked(() => {
            this.tiffPreviewSafeUrl.set(this.sanitizer.bypassSecurityTrustUrl(dataUrl));
            this.tiffDecodePhase.set('ready');
          });
        } catch {
          if (opId !== this.tiffOpId) {
            return;
          }
          untracked(() => {
            this.tiffDecodePhase.set('error');
            this.tiffPreviewSafeUrl.set(null);
          });
        }
      })();
    });
  }

  readonly title = input<string>('');
  readonly mimeType = input<string>('');
  readonly srcUrl = input<string>('');
  readonly srcData = input<Uint8Array | null>(null);
  readonly textContent = input<string>('');
  readonly unsupportedMessage = input<string>('');
  readonly loading = input<boolean>(false);
  readonly errorMessage = input<string>('');
  readonly showDownloadAction = input<boolean>(false);
  readonly downloadLabel = input<string>('Descargar');
  readonly downloadFile = output<void>();
  readonly closePreview = output<void>();

  readonly isPdf = computed(() => this.mimeType().includes('pdf'));
  readonly isImage = computed(() => this.mimeType().startsWith('image/'));
  /** MIME `image/tiff` o extensión .tif / .tiff (p. ej. si el blob venía sin tipo). */
  readonly isTiff = computed(() => {
    const m = (this.mimeType() || '').toLowerCase();
    if (m.includes('tiff')) {
      return true;
    }
    const n = (this.title() || '').toLowerCase();
    return n.endsWith('.tif') || n.endsWith('.tiff');
  });
  readonly isText = computed(
    () =>
      this.mimeType().startsWith('text/') ||
      this.mimeType().includes('json') ||
      this.mimeType().includes('xml'),
  );

  readonly canZoomIn = computed(
    () => this.imageZoom() < VaultFilePreviewComponent.ZOOM_MAX - 1e-9,
  );
  readonly canZoomOut = computed(
    () => this.imageZoom() > VaultFilePreviewComponent.ZOOM_MIN + 1e-9,
  );
  readonly zoomPercentLabel = computed(() => `${Math.round(this.imageZoom() * 100)} %`);

  /** Blob/http(s) en &lt;iframe&gt; / &lt;object data&gt; exigen URL marcada como segura (NG0904). */
  readonly safeResourceUrl = computed<SafeResourceUrl | null>(() => {
    const u = this.srcUrl();
    if (!u) return null;
    return this.sanitizer.bypassSecurityTrustResourceUrl(u);
  });

  /** Blob/http(s) en &lt;img src&gt; */
  readonly safeUrl = computed<SafeUrl | null>(() => {
    const u = this.srcUrl();
    if (!u) return null;
    return this.sanitizer.bypassSecurityTrustUrl(u);
  });

  /** URL efectiva del &lt;img&gt;: original o placeholder tras `(error)`. */
  readonly displayImageUrl = computed<SafeUrl | null>(() => {
    if (this.imageUseFallback()) {
      return this.sanitizer.bypassSecurityTrustUrl(VaultFilePreviewComponent.IMAGE_FALLBACK_SRC);
    }
    return this.safeUrl();
  });

  onImageError(): void {
    if (this.imageUseFallback()) {
      return;
    }
    this.imageUseFallback.set(true);
  }

  zoomIn(): void {
    const next = Math.min(
      VaultFilePreviewComponent.ZOOM_MAX,
      Math.round((this.imageZoom() + VaultFilePreviewComponent.ZOOM_STEP) * 100) / 100,
    );
    this.imageZoom.set(next);
  }

  zoomOut(): void {
    const next = Math.max(
      VaultFilePreviewComponent.ZOOM_MIN,
      Math.round((this.imageZoom() - VaultFilePreviewComponent.ZOOM_STEP) * 100) / 100,
    );
    this.imageZoom.set(next);
  }
}
